'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { campusSvgMap } from '@church/nextjs-ui/website';
import { ApiError, createApi, isUnauthorized, type ProfileApi } from '@/lib/api';
import { createMockApi, type MockPersona } from '@/lib/mock';
import { getMpWidgetToken, onMpTokenChange } from '@/lib/campus';
import type { LocationPlace, ProfileMe, RelationshipRequests } from '@/lib/types';
import { fetchWidgetSettings, resolveSettings, type WidgetSettings } from '@/lib/settings';
import type { ProfileCtx } from './ProfileContext';
import { useToast } from './ui';

export interface ProfileSessionOptions {
  apiBaseUrl: string;
  /** In-memory family instead of apps/api (previews) */
  mock?: boolean;
  /** With `mock`: show the signed-out state */
  mockSignedOut?: boolean;
  /** With `mock`: which family to load */
  mockPersona?: MockPersona;
  /** Explicit overrides from the embed; anything not given comes from MP, then the defaults (see lib/settings) */
  settings?: Partial<WidgetSettings>;
}

export type ProfileStatus = 'loading' | 'ready' | 'signedOut' | 'error';

export interface ProfileSession {
  status: ProfileStatus;
  /** Everything the sections need; null until `status` is `ready` */
  ctx: ProfileCtx | null;
  api: ProfileApi;
  locations: LocationPlace[];
  svgMap: ReturnType<typeof campusSvgMap>;
  mock: boolean;
  /** The links and ids as resolved right now: embed overrides, then MP, then defaults */
  settings: WidgetSettings;
}

/**
 * The signed-in person's session, shared by the profile page and the login
 * widget: the MP widget token is the session, GET /me is the data, and every
 * optimistic write goes through `run`. Must be used under a ToastProvider.
 */
export function useProfileSession({
  apiBaseUrl,
  mock = false,
  mockSignedOut = false,
  mockPersona,
  settings: overrides,
}: ProfileSessionOptions): ProfileSession {
  const toast = useToast();
  const api: ProfileApi = useMemo(
    () =>
      mock
        ? createMockApi({ signedOut: mockSignedOut, persona: mockPersona })
        : createApi(apiBaseUrl),
    [mock, mockSignedOut, mockPersona, apiBaseUrl]
  );
  const [token, setToken] = useState<string | null>(() =>
    mock ? (mockSignedOut ? null : 'mock') : getMpWidgetToken()
  );
  const [status, setStatus] = useState<ProfileStatus>(token ? 'loading' : 'signedOut');
  const [data, setData] = useState<ProfileMe | null>(null);
  const [locations, setLocations] = useState<LocationPlace[]>([]);
  const [requests, setRequests] = useState<RelationshipRequests | null | 'error'>(null);
  const dataRef = useRef<ProfileMe | null>(null);
  dataRef.current = data;
  const [mpSettings, setMpSettings] = useState<Partial<WidgetSettings> | null>(null);

  // The church's links, once, public; until they land (or if they never do) the defaults hold.
  useEffect(() => {
    if (mock) return;
    const controller = new AbortController();
    fetchWidgetSettings(apiBaseUrl, controller.signal)
      .then((s) => {
        if (!controller.signal.aborted) setMpSettings(s);
      })
      .catch(() => {
        /* defaults stand */
      });
    return () => controller.abort();
  }, [apiBaseUrl, mock]);

  // The header login modal, another tab, expiry: the token is the session.
  useEffect(() => {
    if (mock) return;
    return onMpTokenChange(setToken);
  }, [mock]);

  // Campus brand marks and coordinates, public, once.
  useEffect(() => {
    const controller = new AbortController();
    api
      .getLocations(controller.signal)
      .then((l) => setLocations(l as LocationPlace[]))
      .catch(() => {
        /* icons fall back to the Woodside mark */
      });
    return () => controller.abort();
  }, [api]);

  // GET /me first; the sections load the rest in parallel once it lands.
  useEffect(() => {
    if (!token) {
      setStatus('signedOut');
      setData(null);
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    api
      .getMe(controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setData(d);
        setStatus('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (isUnauthorized(err)) {
          setStatus('signedOut');
          return;
        }
        console.error('Profile widget failed to load:', err);
        setStatus('error');
      });
    return () => controller.abort();
  }, [api, token]);

  const update = useCallback((fn: (d: ProfileMe) => ProfileMe) => {
    setData((d) => (d ? fn(d) : d));
  }, []);
  const patchRequests = useCallback((fn: (r: RelationshipRequests) => RelationshipRequests) => {
    setRequests((r) => (r && r !== 'error' ? fn(r) : r));
  }, []);

  const run = useCallback<ProfileCtx['run']>(
    async (req, revert, okMessage) => {
      try {
        await req();
        if (okMessage) toast(okMessage, 'ok');
        return true;
      } catch (err) {
        revert?.();
        if (isUnauthorized(err)) {
          setToken(null);
          setStatus('signedOut');
          return false;
        }
        toast(err instanceof ApiError ? err.message : 'Could not save that. Try again.');
        return false;
      }
    },
    [toast]
  );

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);
  // Keyed on the overrides' content, not their identity: a parent may hand a fresh object each render.
  const overridesKey = JSON.stringify(overrides ?? {});
  const settings = useMemo(
    () => resolveSettings(overrides, mpSettings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overridesKey, mpSettings]
  );
  const urls = useMemo(
    () => ({
      groupFinder: settings.groupFinderUrl,
      give: settings.giveUrl,
      pay: settings.payUrl,
      manageGiving: settings.manageGivingUrl,
    }),
    [settings]
  );
  const seeJesusCampaignId = settings.seeJesusCampaignId;

  const ctx: ProfileCtx | null =
    status === 'ready' && data
      ? {
          api,
          data,
          update,
          run,
          urls,
          seeJesusCampaignId,
          mock,
          requests,
          setRequests,
          patchRequests,
        }
      : null;

  return { status, ctx, api, locations, svgMap, mock, settings };
}
